import React, { useId, useMemo } from 'react'
import { Flex, Select, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import type { ValueInputProps } from '@pushly/aqe'
import { useSegmentValueInputContext } from './segment-value-input-context'
import {
    buildJourneyTree,
    deriveJourneyState,
    JOURNEY_STATUSES,
    JOURNEY_TRIGGERS,
    type JourneySelectionType,
    type JourneyStatusState,
} from './journey-status-tree'

/**
 * The statuses tooltip, verbatim from the legacy widget. It explains what each status matches,
 * which is not otherwise obvious from the labels alone.
 */
const STATUS_TOOLTIP = (
    <div>
        The following statuses match all subscribers who currently meet the stated requirements:
        <ul>
            <li>Entered: Entered the journey regardless of their current status.</li>
            <li>Active: Are currently active in the journey.</li>
            <li>
                Removed: Were removed from the journey before it completed or were still active in a journey that was
                configured to set all active members' status to "Removed" when it was completed.
            </li>
            <li>
                Completed: Completed the journey or were still active in a journey that was configured to set all active
                members' status to "Completed" when it finished.
            </li>
        </ul>
    </div>
)

/**
 * What each control on this surface is called.
 *
 * Named once because two places state them: the editor's own labels, and the summary a collapsed
 * row reads the same choices back as. A second copy of the wording is how the two come to disagree
 * about what a reader picked.
 */
export const JOURNEY_LABELS = {
    journey: 'Journey',
    trigger: 'Trigger',
    operator: 'Operator',
    status: 'Status',
} as const

/** Which kind of journey match the condition makes, in the order the reader sees them. */
export const JOURNEY_SELECTION_TYPES: ReadonlyArray<{ value: JourneySelectionType; label: string }> = [
    { value: 'any', label: 'Any Journey' },
    { value: 'triggered', label: 'Journey with Trigger' },
    { value: 'specific', label: 'Specific Journey' },
]

/**
 * The equality wording the whole app states its conditions in. The values are the engine's own and
 * are untouched: only what the reader is shown changes.
 */
export const JOURNEY_OPERATORS: ReadonlyArray<{ value: 'eq' | 'neq'; label: string }> = [
    { value: 'eq', label: 'is exactly' },
    { value: 'neq', label: 'is not' },
]

/**
 * The value control for a journey-status condition — the one field whose value is a nested rule
 * tree rather than a scalar.
 *
 * The builder hands this renderer the inner condition group as `value` and takes the inner group
 * back through `onChange`; serialization and validation stay store-owned. Rather than edit that
 * tree in place, the renderer reduces it to a derived state (which kind of match, the journey or
 * trigger, an equality operator, and a set of statuses) and rebuilds the whole tree from that
 * state on every change — see `journey-status-tree`. Each control therefore reads its own slice of
 * the derived state and emits a freshly built tree, so the coupling between operator and statuses
 * is decided in one place instead of spread across per-rule edits.
 *
 * The journey list is loaded once for the whole builder and read from context, the same way the
 * prompt picker resolves a stored id: a reloaded condition holds only a journey id, and the list is
 * what turns it back into a name the reader recognizes.
 */
export function JourneyStatusInput({
    value,
    onChange,
    disabled,
    error,
    size,
    getPopupContainer,
}: ValueInputProps<unknown>) {
    const { journeys, journeysLoading } = useSegmentValueInputContext()

    const idBase = useId()
    const journeyId = `${idBase}-journey`
    const triggerId = `${idBase}-trigger`
    const operatorId = `${idBase}-operator`
    const statusId = `${idBase}-status`

    const state = useMemo(() => deriveJourneyState(value), [value])
    const emit = (next: JourneyStatusState) => onChange(buildJourneyTree(next))

    const journeyOptions = useMemo(() => {
        const resolved = journeys.map((journey) => ({ value: journey.id, label: journey.name }))

        // A stored id the loaded list does not hold — a journey since deleted — still has to render
        // rather than blank the row, and only the id is left to render it by: a name reaches the
        // condition solely by being picked out of this list, so an id absent from the list never
        // had one to keep.
        if (state.journeyId !== undefined && !journeys.some((journey) => journey.id === state.journeyId)) {
            return [{ value: state.journeyId, label: `#${state.journeyId}` }, ...resolved]
        }

        return resolved
    }, [journeys, state.journeyId])

    const handleSelectionType = (selectionType: JourneySelectionType) => {
        // Switching the kind of match drops whichever front rule the previous kind carried and
        // keeps the status choices — the same operator and statuses now apply to the new match.
        emit({ ...state, selectionType, journeyId: undefined, triggerType: undefined })
    }

    const handleJourney = (journeyId: number) => {
        emit({ ...state, selectionType: 'specific', journeyId, triggerType: undefined })
    }

    const handleTrigger = (triggerType: string) => {
        emit({ ...state, selectionType: 'triggered', triggerType, journeyId: undefined })
    }

    const handleOperator = (operator: 'eq' | 'neq') => {
        emit({ ...state, operator })
    }

    const handleStatuses = (statuses: string[]) => {
        emit({ ...state, statuses })
    }

    return (
        <Flex className="segment-journey-status" vertical gap="small">
            <Flex className="segment-journey-status__field" vertical gap={4}>
                <label htmlFor={journeyId}>{JOURNEY_LABELS.journey}</label>
                <Select
                    id={journeyId}
                    className="segment-journey-status__selection-type"
                    value={state.selectionType}
                    onChange={handleSelectionType}
                    disabled={disabled}
                    size={size}
                    getPopupContainer={getPopupContainer}
                    options={JOURNEY_SELECTION_TYPES.map((type) => ({ value: type.value, label: type.label }))}
                />

                {state.selectionType === 'specific' && (
                    <Select
                        className="segment-journey-status__journey"
                        showSearch={true}
                        optionFilterProp="label"
                        placeholder="Select a Journey"
                        loading={journeysLoading}
                        value={state.journeyId}
                        onChange={handleJourney}
                        disabled={disabled}
                        size={size}
                        status={error ? 'error' : undefined}
                        getPopupContainer={getPopupContainer}
                        options={journeyOptions}
                    />
                )}
            </Flex>

            {state.selectionType === 'triggered' && (
                <Flex className="segment-journey-status__field" vertical gap={4}>
                    <label htmlFor={triggerId}>{JOURNEY_LABELS.trigger}</label>
                    <Select
                        id={triggerId}
                        className="segment-journey-status__trigger"
                        placeholder="Select a Trigger"
                        value={state.triggerType}
                        onChange={handleTrigger}
                        disabled={disabled}
                        size={size}
                        status={error ? 'error' : undefined}
                        getPopupContainer={getPopupContainer}
                        options={JOURNEY_TRIGGERS.map((trigger) => ({ value: trigger.value, label: trigger.label }))}
                    />
                </Flex>
            )}

            <Flex className="segment-journey-status__field" vertical gap={4}>
                <label htmlFor={operatorId}>{JOURNEY_LABELS.operator}</label>
                <Select
                    id={operatorId}
                    className="segment-journey-status__operator"
                    value={state.operator}
                    onChange={handleOperator}
                    disabled={disabled}
                    size={size}
                    getPopupContainer={getPopupContainer}
                    options={JOURNEY_OPERATORS.map((option) => ({ value: option.value, label: option.label }))}
                />
            </Flex>

            <Flex className="segment-journey-status__field" vertical gap={4}>
                <Flex gap={4} align="center">
                    <label htmlFor={statusId}>{JOURNEY_LABELS.status}</label>
                    <Tooltip classNames={{ root: 'segment-journey-status__tooltip' }} title={STATUS_TOOLTIP}>
                        <InfoCircleOutlined className="segment-journey-status__info" />
                    </Tooltip>
                </Flex>
                <Select
                    id={statusId}
                    className="segment-journey-status__status"
                    mode="multiple"
                    placeholder="Select a Status"
                    value={state.statuses}
                    onChange={handleStatuses}
                    disabled={disabled}
                    size={size}
                    status={error ? 'error' : undefined}
                    getPopupContainer={getPopupContainer}
                    options={JOURNEY_STATUSES.map((status) => ({ value: status.value, label: status.label }))}
                />
            </Flex>
        </Flex>
    )
}
